import { Component, forwardRef, Input, OnInit, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BranchItemResponse } from '../../../../../api';

@Component({
  selector: 'base-service-provider-dropdown',
  templateUrl: './base-service-provider-dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BaseServiceProviderDropdownComponent),
      multi: true,
    },
  ],
})
export class BaseServiceProviderDropdownComponent implements ControlValueAccessor, OnInit {
  @Input() initialItems: BranchItemResponse[] = [];

  value: BranchItemResponse[] = [];
  visibleItemsAfterSearch: BranchItemResponse[] = [];
  isDropdownOpen = false;
  extendedView = false;
  isSearchActive = false;
  isDisabled = false;
  items: (BranchItemResponse & { selectAll: string })[] = [];

  constructor(protected translate: TranslateService, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.items = this.initialItems.map((item) => {
      return {
        ...item,
        selectAll: this.translate.instant('selectDeselectServiceProvider.selectAll'),
      };
    });
  }

  get selectedCount(): number {
    return this.value.length;
  }

  get columnsCount(): number {
    if (!this.extendedView) return 1;
    const count = this.isSearchActive ? this.visibleItemsAfterSearch.length : this.items.length;
    if (count >= 5 && count <= 9) return 2;
    if (count >= 10 && count <= 23) return 3;
    if (count >= 24) return 4;
    return 1;
  }

  get allSelected(): boolean {
    return this.items.length > 0 && this.selectedCount === this.items.length;
  }

  onSelectChange(selected: BranchItemResponse[]): void {
    this.value = selected;
    this.onChange(this.value);
  }

  writeValue(obj: any[]): void {
    this.value = obj ?? [];
  }

  onDropdownOpen() {
    this.isDropdownOpen = true;

    setTimeout(() => {
      const panel = document.querySelector('.ng-dropdown-panel-items.scroll-host');
      if (panel) {
        this.renderer.setProperty(panel, 'scrollTop', 0);
      }
    });
  }

  onCloseDropdown() {
    this.isDropdownOpen = false;

    this.isSearchActive = false;
    this.extendedView = false;
    this.onTouched();
  }

  onSearch(data: { term: string; items: any[] }) {
    this.isSearchActive = !!data.term;
    this.visibleItemsAfterSearch = data.items || [];
  }

  toggleSelectAll(isAllSelected: boolean) {
    this.value = isAllSelected ? [...this.items] : [];
    this.onChange(this.value);
  }

  toggleExtendView(event: MouseEvent) {
    event.preventDefault();
    this.extendedView = !this.extendedView;
  }

  compareItems(item1: BranchItemResponse | null, item2: BranchItemResponse | null): boolean {
    if (!item1 || !item2) {
      return item1 === item2;
    }
    return item1.id === item2.id;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  registerOnChange(fn: (value: any) => any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  private onChange: (value: any[]) => void = () => {};
  private onTouched: () => void = () => {};
}
